import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import FeaturedVerseHero from './FeaturedVerseHero';
import TopSharedVerses from './TopSharedVerses';
import { 
  Users, 
  UserPlus, 
  Search, 
  Settings, 
  Heart,
  MessageCircle,
  BookMarked,
  Image as ImageIcon,
  Flame,
  BookOpen,
  Check,
  X,
  UserMinus,
  Bell,
  Video,
  Play,
  ArrowUpRight,
  ArrowLeft,
  Gamepad2,
  Church,
  Images
} from 'lucide-react';
import axios from 'axios';
import VerseGalleryTab from './community/VerseGalleryTab';
import { toast } from 'sonner';
import { messagesApi } from '../services/messagesApi';
import { formatDistanceToNow } from 'date-fns';
import GamesTab from './games/GamesTab';
import PostFeed from './PostFeed';
import ProfileSheet from './ProfileSheet';
import NotificationBell from './community/NotificationBell';
import CommunityPulseCard from './CommunityPulseCard';
import RecapAutoPost from './RecapAutoPost';
import { useAuthGate } from '../hooks/useAuthGate';
import ChurchesView from './ChurchesView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Track which notification ids we've already surfaced as a live toast so we
// never re-toast the same one across polls / sessions (kept in localStorage).
const TOASTED_KEY = 'lumera_toasted_notifs';
const loadToastedSet = () => {
  try { return new Set(JSON.parse(localStorage.getItem(TOASTED_KEY) || '[]')); }
  catch { return new Set(); }
};
const saveToastedSet = (set) => {
  try { localStorage.setItem(TOASTED_KEY, JSON.stringify(Array.from(set).slice(-300))); }
  catch { /* ignore */ }
};

const CommunityPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { gate, requireAuth, dialog, isGuest } = useAuthGate();
  const { language, t } = useLanguage();
  const [searchParams] = useSearchParams();

  // Default tab honors `?tab=games`, `?sundae=...`, and `?pastor=...` deep links.
  const initialTab = (() => {
    const tab = searchParams.get('tab');
    if (tab === 'games' || searchParams.get('sundae') || searchParams.get('pastor')) return 'games';
    if (tab === 'gallery') return 'gallery';
    return 'feed';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activities, setActivities] = useState([]);
  const [friends, setFriends] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [profileUser, setProfileUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [privacy, setPrivacy] = useState({
    share_highlights: true,
    share_verse_images: true,
    share_reading_streak: true,
    share_notes: false,
    share_reading_plans: true,
    share_prayers: false
  });
  const [loading, setLoading] = useState(false);
  const [commentInputs, setCommentInputs] = useState({}); // Track comment input per activity
  const [showComments, setShowComments] = useState({}); // Track which activities show comments

  // Featured spiritual content (YouTube videos)
  const featuredVideos = [
    {
      id: 1,
      title: t('community.vid1Title'),
      minister: "Apóstol Sergio Enríquez",
      youtubeId: "7iKKaKNUHmI", // Real video: Segundo servicio devocional (March 2026)
      thumbnail: `https://img.youtube.com/vi/7iKKaKNUHmI/maxresdefault.jpg`,
      description: t('community.vid1Desc')
    },
    {
      id: 2,
      title: t('community.vid2Title'),
      minister: "Apóstol Sergio Enríquez",
      youtubeId: "m8j-0eEvVnM", // Real video: Servicio de Oración (April 2026)
      thumbnail: `https://img.youtube.com/vi/m8j-0eEvVnM/maxresdefault.jpg`,
      description: t('community.vid2Desc')
    },
    {
      id: 3,
      title: "Supernatural Breakthrough - Miracles Documented",
      minister: "Nathan Morris - Shake The Nations",
      youtubeId: "2A7yJ0EKQ0s", // Real video: 2024 Highlight Video (April 2025)
      thumbnail: `https://img.youtube.com/vi/2A7yJ0EKQ0s/maxresdefault.jpg`,
      description: t('community.vid3Desc')
    },
    {
      id: 4,
      title: "Signs, Wonders & Miracles",
      minister: "Nathan Morris - Shake The Nations",
      youtubeId: "g8jHk6P75B0", // Real video: Ordinary People See Miracles
      thumbnail: `https://img.youtube.com/vi/g8jHk6P75B0/maxresdefault.jpg`,
      description: t('community.vid4Desc')
    }
  ];

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Weekly gentle nudge to keep praying for a group's open requests.
  useEffect(() => {
    if (!user) return;
    const KEY = 'lumera_prayer_nudge';
    const last = parseInt(localStorage.getItem(KEY) || '0', 10);
    if (Date.now() - last < 7 * 24 * 60 * 60 * 1000) return;
    let cancelled = false;
    (async () => {
      try {
        const digest = await messagesApi.prayerDigest();
        if (cancelled || !digest || digest.length === 0) return;
        const total = digest.reduce((s, g) => s + (g.open_count || 0), 0);
        localStorage.setItem(KEY, String(Date.now()));
        toast(total === 1
          ? t('community.openPrayerReqsOne', { total })
          : t('community.openPrayerReqsMany', { total }), {
          duration: 8000,
          action: {
            label: t('community.open'),
            onClick: () => window.dispatchEvent(new CustomEvent('lumera:open-messages')),
          },
        });
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, language]);


  // Surface "your friend request was accepted" notifications once per visit.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/community/notifications?unread_only=false`, { withCredentials: true });
        const notes = res.data?.notifications || [];
        if (cancelled) return;
        setNotifications(notes);
        setUnreadCount(notes.filter((x) => !x.read).length);
        const toasted = loadToastedSet();
        notes.filter((x) => !x.read && !toasted.has(x.id)).forEach((n) => {
          if (n.type === 'friend_accepted') {
            toast.success(t('community.friendAccepted', { name: n.actor_name }));
          } else if (n.type === 'new_follower') {
            toast.success(t('community.newFollower', { name: n.actor_name }));
          } else if (n.type === 'activity_liked') {
            const ctxKeys = ['verse_image', 'highlight', 'note', 'reading_streak', 'reading_plan', 'prayer'];
            const label = ctxKeys.includes(n.context) ? t(`community.ctx.${n.context}`) : t('community.ctx.post');
            toast.success(t('community.activityLiked', { name: n.actor_name, label }));
          } else if (n.type === 'story_reaction') {
            toast.success(`${n.actor_name} ${n.emoji || '❤️'} ${t('community.storyReaction')}`);
          } else if (n.type === 'story_reply') {
            toast(t('community.storyReply', { name: n.actor_name }));
          } else if (n.type === 'new_message') {
            toast(t('community.newMessage', { name: n.actor_name }));
          } else if (n.type === 'group_message') {
            toast(t('community.groupMessage', { name: n.actor_name }));
          } else if (n.type === 'group_mention') {
            toast(t('community.groupMention', { name: n.actor_name }));
          } else if (n.type === 'prayer_prayed') {
            toast(t('community.prayerPrayed', { name: n.actor_name }));
          } else if (n.type === 'prayer_answered') {
            toast.success(t('community.prayerAnswered'));
          } else if (n.type === 'group_added') {
            toast.success(t('community.groupAdded', { name: n.actor_name }));
          } else if (n.type === 'post_liked') {
            toast.success(t('community.postLiked', { name: n.actor_name }));
          } else if (n.type === 'post_commented') {
            toast(t('community.postCommented', { name: n.actor_name }));
          } else if (n.type === 'post_reaction') {
            toast(`${n.actor_name} ${n.emoji || '🙏'} ${t('community.postReaction')}`);
          } else if (n.type === 'freeze_used') {
            toast(t('community.freezeUsed'));
          } else if (n.type === 'partner_invite') {
            toast(t('community.partnerInvite', { name: n.actor_name }));
          } else if (n.type === 'partner_accepted') {
            toast.success(t('community.partnerAccepted', { name: n.actor_name }));
          } else if (n.type === 'partner_streak') {
            toast(t('community.partnerStreak', { streak: n.shared_streak }));
          } else if (n.type === 'partner_nudge') {
            toast(t('community.partnerNudge', { name: n.actor_name }), {
              duration: 8000,
              action: {
                label: t('community.pray'),
                onClick: () => window.dispatchEvent(new CustomEvent('lumera:open-messages')),
              },
              cancel: {
                label: t('community.snooze'),
                onClick: () => messagesApi.snoozeNudge()
                  .then(() => toast.success(t('community.snoozed')))
                  .catch(() => {}),
              },
            });
          } else if (n.type === 'admin_warning') {
            toast.warning(t('community.moderatorNotice') + (n.message || ''));
          } else if (n.type === 'mention') {
            toast(t('community.mentionedYou', { name: n.actor_name }));
          } else if (n.type === 'activity_commented') {
            toast(t('community.postCommented', { name: n.actor_name }));
          }
          toasted.add(n.id);
        });
        saveToastedSet(toasted);
      } catch (_) { /* non-blocking */ }
    };
    poll();
    const intervalId = setInterval(poll, 20000);
    const onVisible = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, language]);

  const markAllNotificationsRead = async () => {
    const toasted = loadToastedSet();
    notifications.forEach((n) => toasted.add(n.id));
    saveToastedSet(toasted);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await axios.post(`${BACKEND_URL}/api/community/notifications/read`, {}, { withCredentials: true });
    } catch (_) { /* non-blocking */ }
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'feed') {
        await loadActivityFeed();
      } else if (activeTab === 'friends') {
        if (!user) return;
        await loadFriends();
        await loadFriendRequests();
      } else if (activeTab === 'following') {
        if (!user) return;
        await loadFollowers();
        await loadFollowing();
      } else if (activeTab === 'privacy') {
        if (!user) return;
        await loadPrivacySettings();
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityFeed = async () => {
    if (!user) { setActivities([]); return; }
    const response = await axios.get(`${BACKEND_URL}/api/community/feed?limit=50`, {
      withCredentials: true
    });
    setActivities(response.data.activities);
  };

  const loadFriends = async () => {
    const response = await axios.get(`${BACKEND_URL}/api/community/friends`, {
      withCredentials: true
    });
    setFriends(response.data.friends);
  };

  const loadFriendRequests = async () => {
    const response = await axios.get(`${BACKEND_URL}/api/community/friend-requests?request_type=received`, {
      withCredentials: true
    });
    setFriendRequests(response.data.requests);
  };

  const loadFollowers = async () => {
    const response = await axios.get(`${BACKEND_URL}/api/community/followers`, {
      withCredentials: true
    });
    setFollowers(response.data.followers);
  };

  const loadFollowing = async () => {
    const response = await axios.get(`${BACKEND_URL}/api/community/following`, {
      withCredentials: true
    });
    setFollowing(response.data.following);
  };

  const loadPrivacySettings = async () => {
    const response = await axios.get(`${BACKEND_URL}/api/community/privacy`, {
      withCredentials: true
    });
    setPrivacy(response.data);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/community/search?q=${encodeURIComponent(searchQuery)}`,
        { withCredentials: true }
      );
      setSearchResults(response.data.users);
    } catch (error) {
      toast.error(t('community.searchError'));
    }
  };

  const handleSendFriendRequest = async (userId) => {
    if (!requireAuth(t('community.addingFriends'))) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/community/friend-request/send`,
        { user_id: userId },
        { withCredentials: true }
      );
      toast.success(t('community.requestSent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('community.requestSendError'));
    }
  };

  const handleRespondToRequest = async (requestId, accept) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/community/friend-request/respond`,
        { request_id: requestId, accept },
        { withCredentials: true }
      );
      toast.success(accept 
        ? t('community.requestAccepted')
        : t('community.requestRejected')
      );
      loadFriendRequests();
      if (accept) loadFriends();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('community.respondError'));
    }
  };

  const handleFollowUser = async (userId) => {
    if (!requireAuth(t('community.followingUsers'), { type: 'follow_user', target: userId })) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/community/follow/${userId}`,
        {},
        { withCredentials: true }
      );
      toast.success(t('community.followingUser'));
      loadFollowing();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('community.followError'));
    }
  };

  const handleUnfollowUser = async (userId) => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/community/follow/${userId}`,
        { withCredentials: true }
      );
      toast.success(t('community.unfollowed'));
      loadFollowing();
    } catch (error) {
      toast.error(t('community.unfollowError'));
    }
  };

  const handleUpdatePrivacy = async (key, value) => {
    const newSettings = { ...privacy, [key]: value };
    setPrivacy(newSettings);
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/community/privacy`,
        newSettings,
        { withCredentials: true }
      );
      toast.success(t('community.privacyUpdated'));
    } catch (error) {
      toast.error(t('community.privacyUpdateError'));
    }
  };

  const handleLikeActivity = async (activityId) => {
    if (!requireAuth(t('community.likingPosts'), { type: 'like_activity', target: activityId })) return;
    try {
      const activity = activities.find(a => a.id === activityId);
      const isLiked = activity?.likes?.includes(user.user_id);

      if (isLiked) {
        // Unlike
        await axios.delete(
          `${BACKEND_URL}/api/community/activity/${activityId}/like`,
          { withCredentials: true }
        );
        setActivities(activities.map(a => 
          a.id === activityId 
            ? { ...a, likes: a.likes.filter(id => id !== user.user_id), likes_count: (a.likes_count || 1) - 1 }
            : a
        ));
      } else {
        // Like
        await axios.post(
          `${BACKEND_URL}/api/community/activity/${activityId}/like`,
          {},
          { withCredentials: true }
        );
        setActivities(activities.map(a => 
          a.id === activityId 
            ? { ...a, likes: [...(a.likes || []), user.user_id], likes_count: (a.likes_count || 0) + 1 }
            : a
        ));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('community.likeError'));
    }
  };

  const handleAddComment = async (activityId) => {
    const commentText = commentInputs[activityId];
    if (!requireAuth(t('community.commenting'), commentText?.trim() ? { type: 'comment_activity', target: activityId, text: commentText.trim() } : null)) return;
    if (!commentText || !commentText.trim()) return;

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/community/activity/${activityId}/comment`,
        { text: commentText.trim() },
        { withCredentials: true }
      );

      // Update activity with new comment
      setActivities(activities.map(a => 
        a.id === activityId 
          ? { 
              ...a, 
              comments: [...(a.comments || []), response.data.comment],
              comments_count: (a.comments_count || 0) + 1 
            }
          : a
      ));

      // Clear input
      setCommentInputs({ ...commentInputs, [activityId]: '' });
      toast.success(t('community.commentAdded'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('community.commentError'));
    }
  };

  const toggleComments = (activityId) => {
    setShowComments({ ...showComments, [activityId]: !showComments[activityId] });
  };

  const renderActivityCard = (activity) => {
    const getActivityIcon = (type) => {
      switch (type) {
        case 'highlight': return <Heart className="w-5 h-5 text-yellow-500" />;
        case 'verse_image': return <ImageIcon className="w-5 h-5 text-purple-500" />;
        case 'reading_streak': return <Flame className="w-5 h-5 text-orange-500" />;
        case 'note': return <MessageCircle className="w-5 h-5 text-blue-500" />;
        case 'reading_plan': return <BookMarked className="w-5 h-5 text-green-500" />;
        case 'prayer': return <Heart className="w-5 h-5 text-pink-500" />;
        default: return <BookOpen className="w-5 h-5 text-slate-500" />;
      }
    };

    const getActivityText = (act) => {
      const name = act.user?.name || 'User';
      switch (act.type) {
        case 'highlight':
          return t('community.actHighlight', { name });
        case 'verse_image':
          return t('community.actVerseImage', { name });
        case 'reading_streak':
          return t('community.actReadingStreak', { name, days: act.data?.days || 0 });
        case 'note':
          return t('community.actNote', { name });
        case 'reading_plan':
          return t('community.actReadingPlan', { name });
        case 'prayer':
          return t('community.actPrayer', { name });
        default:
          return t('community.actDefault', { name });
      }
    };

    return (
      <Card key={activity.id || activity.created_at} className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              {getActivityIcon(activity.type)}
            </div>
            <div className="flex-1">
              <p className="font-medium text-sm mb-1">{getActivityText(activity)}</p>
              
              {activity.data?.verse && (
                <div className="bg-muted/30 p-3 rounded-lg mt-2">
                  <p className="text-xs font-semibold text-primary mb-1">
                    {activity.data.verse.reference}
                  </p>
                  <p className="text-sm text-foreground">
                    &ldquo;{activity.data.verse.text}&rdquo;
                  </p>
                  {activity.data.color && (
                    <Badge className="mt-2" style={{ backgroundColor: activity.data.color }}>
                      {activity.data.color}
                    </Badge>
                  )}
                </div>
              )}

              {activity.data?.image_url && (
                <img 
                  src={activity.data.image_url} 
                  alt="Verse" 
                  className="mt-2 rounded-lg max-w-sm"
                />
              )}

              <p className="text-xs text-muted-foreground mt-2">
                {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
              </p>

              {/* Likes and Comments Actions */}
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
                {/* Like Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-1.5 text-muted-foreground hover:text-pink-600 dark:hover:text-pink-400"
                  onClick={() => handleLikeActivity(activity.id)}
                >
                  <Heart 
                    className={`w-4 h-4 ${activity.likes?.includes(user.user_id) ? 'fill-pink-600 text-pink-600' : ''}`} 
                  />
                  <span className="text-xs font-medium">
                    {activity.likes_count || 0} {t('community.likes')}
                  </span>
                </Button>

                {/* Comment Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-1.5 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400"
                  onClick={() => toggleComments(activity.id)}
                >
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-xs font-medium">
                    {activity.comments_count || 0} {t('community.comments')}
                  </span>
                </Button>
              </div>

              {/* Comments Section */}
              {showComments[activity.id] && (
                <div className="mt-4 space-y-3">
                  {/* Existing Comments */}
                  {activity.comments && activity.comments.length > 0 && (
                    <div className="space-y-2">
                      {activity.comments.map((comment) => (
                        <div key={comment.id} className="bg-muted/20 p-2.5 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-foreground">
                                {comment.user?.name || 'User'}
                              </p>
                              <p className="text-sm text-foreground mt-1">{comment.text}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Comment Input */}
                  <div className="flex gap-2">
                    <Input
                      placeholder={t('community.writeComment')}
                      value={commentInputs[activity.id] || ''}
                      onChange={(e) => setCommentInputs({ ...commentInputs, [activity.id]: e.target.value })}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddComment(activity.id);
                        }
                      }}
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      onClick={() => handleAddComment(activity.id)}
                      disabled={!commentInputs[activity.id]?.trim()}
                    >
                      {t('community.send')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          {/* Back Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/'))}
            className="hover:bg-accent"
            data-testid="community-back-btn"
            title={t('community.goBack')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          <Users className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold">
            {t('community.title')}
          </h1>
          {user && (
            <div className="ml-auto">
              <NotificationBell
                notifications={notifications}
                unreadCount={unreadCount}
                onOpenMarkRead={markAllNotificationsRead}
                onOpenProfile={(uid) => setProfileUser(uid)}
                language={language}
              />
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Mobile: one swipeable row (the old 4-col grid wrapped into a clipped 2nd row); desktop: 7-col grid */}
          <TabsList className="mb-6 flex h-auto w-full justify-start gap-1 overflow-x-auto p-1 sm:grid sm:grid-cols-8" data-testid="community-tabs">
            <TabsTrigger value="feed" className="shrink-0 text-xs sm:text-sm" data-testid="community-tab-feed">
              <BookOpen className="w-4 h-4 mr-1" />
              {t('community.tabFeed')}
            </TabsTrigger>
            <TabsTrigger value="gallery" className="shrink-0 text-xs sm:text-sm" data-testid="community-tab-gallery">
              <Images className="w-4 h-4 mr-1" />
              {language === 'es' ? 'Galería' : 'Gallery'}
            </TabsTrigger>
            <TabsTrigger value="games" className="shrink-0 text-xs sm:text-sm" data-testid="community-tab-games">
              <Gamepad2 className="w-4 h-4 mr-1" />
              {t('community.tabGames')}
            </TabsTrigger>
            <TabsTrigger value="videos" className="shrink-0 text-xs sm:text-sm" data-testid="community-tab-videos">
              <Video className="w-4 h-4 mr-1" />
              {t('community.tabVideos')}
            </TabsTrigger>
            <TabsTrigger value="friends" className="shrink-0 text-xs sm:text-sm relative" data-testid="community-tab-friends">
              <Users className="w-4 h-4 mr-1" />
              {t('community.tabFriends')}
              {friendRequests.length > 0 && (
                <Badge className="absolute top-0 right-0 h-4 w-4 p-0 flex items-center justify-center bg-red-500 text-white text-[10px]">
                  {friendRequests.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="following" className="shrink-0 text-xs sm:text-sm" data-testid="community-tab-following">
              <UserPlus className="w-4 h-4 mr-1" />
              {t('community.tabFollow')}
            </TabsTrigger>
            <TabsTrigger value="churches" className="shrink-0 text-xs sm:text-sm" data-testid="community-tab-churches">
              <Church className="w-4 h-4 mr-1" />
              {t('community.tabChurches')}
            </TabsTrigger>
            <TabsTrigger value="privacy" className="shrink-0 text-xs sm:text-sm" data-testid="community-tab-privacy">
              <Settings className="w-4 h-4 mr-1" />
              {t('community.tabPrivacy')}
            </TabsTrigger>
          </TabsList>

          {/* Feed Tab */}
          <TabsContent value="feed">
            {/* Silent weekly recap auto-post (opt-in) */}
            <RecapAutoPost language={language} user={user} />
            {/* Weekly community rollup */}
            <CommunityPulseCard language={language} user={user} />

            {/* Instagram-style posts — main community landing experience */}
            <PostFeed
              language={language}
              isGuest={isGuest}
              currentUserId={user?.user_id}
              requireAuth={requireAuth}
            />

            <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
              <span className="flex-1 border-t" />
              {t('community.recentActivity')}
              <span className="flex-1 border-t" />
            </div>

            {/* Trending / social-proof surfaces */}
            <div className="space-y-6 mb-6">
              <FeaturedVerseHero variant="dark" compact />
              <TopSharedVerses inFeed />
            </div>

            <ScrollArea className="h-[calc(100vh-250px)]">
              {loading && <p className="text-center py-8 text-muted-foreground">Loading...</p>}
              
              {!loading && activities.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center" data-testid="community-feed-empty">
                    <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    {isGuest ? (
                      <>
                        <p className="text-muted-foreground mb-4">
                          {t('community.guestFeedPrompt')}
                        </p>
                        <Button
                          onClick={() => requireAuth(t('community.communityFeedLabel'))}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid="community-feed-signin"
                        >
                          {t('community.signInFree')}
                        </Button>
                      </>
                    ) : (
                      <p className="text-muted-foreground">
                        {t('community.noActivities')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {activities.map(renderActivityCard)}
            </ScrollArea>
          </TabsContent>

          {/* Games Tab — Bible Trivia / Verse Scramble / Who Said It? */}
          <TabsContent value="gallery">
            <VerseGalleryTab language={language} />
          </TabsContent>

          <TabsContent value="games">
            <GamesTab language={language} />
          </TabsContent>

          {/* Videos Tab - Spiritual Content */}
          <TabsContent value="videos">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Video className="w-5 h-5 text-primary" />
                    {t('community.spiritualContent')}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {t('community.spiritualContentDesc')}
                  </p>
                </CardHeader>
              </Card>

              {/* Video Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {featuredVideos.map((video) => (
                  <Card key={video.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="relative aspect-video bg-slate-900">
                      {/* YouTube Thumbnail */}
                      <img 
                        src={video.thumbnail} 
                        alt={video.title}
                        className="w-full h-full object-cover opacity-80"
                      />
                      {/* Play Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors cursor-pointer group">
                        <div className="bg-red-600 rounded-full p-4 group-hover:scale-110 transition-transform">
                          <Play className="w-8 h-8 text-white fill-white" />
                        </div>
                      </div>
                      {/* Badge */}
                      <Badge className="absolute top-3 right-3 bg-red-600 text-white">
                        YouTube
                      </Badge>
                    </div>
                    
                    <CardContent className="p-4 space-y-3">
                      <div>
                        <h3 className="font-semibold text-lg mb-1">{video.title}</h3>
                        <p className="text-sm text-amber-600 font-medium">{video.minister}</p>
                      </div>
                      
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {video.description}
                      </p>
                      
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => window.open(`https://www.youtube.com/watch?v=${video.youtubeId}`, '_blank')}
                      >
                        <ArrowUpRight className="w-4 h-4 mr-2" />
                        {t('community.watchOnYouTube')}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Call to Action */}
              <Card className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 border-purple-500/20">
                <CardContent className="p-6 text-center space-y-4">
                  <Heart className="w-12 h-12 mx-auto text-red-500" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">
                      {t('community.shareVideoTitle')}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t('community.shareVideoDesc')}
                    </p>
                  </div>
                  <Button variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    {t('community.contactUs')}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Friends Tab */}
          <TabsContent value="friends">
            <div className="space-y-6">
              {/* Friend Requests */}
              {friendRequests.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="w-5 h-5" />
                      {t('community.pendingRequests')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {friendRequests.map((request) => (
                        <div key={request.from_user} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div>
                            <button
                              type="button"
                              onClick={() => setProfileUser(request.from_user)}
                              className="font-medium text-left hover:underline hover:text-primary transition-colors"
                              data-testid={`request-name-${request.from_user}`}
                            >
                              {request.user?.name}
                            </button>
                            <p className="text-sm text-muted-foreground">{request.user?.email}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleRespondToRequest(request.id, true)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRespondToRequest(request.id, false)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Search Users */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="w-5 h-5" />
                    {t('community.findUsers')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 mb-4">
                    <Input
                      placeholder={t('community.searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button onClick={handleSearch}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>

                  {searchResults.length > 0 && (
                    <div className="space-y-2">
                      {searchResults.map((resultUser) => (
                        <div key={resultUser.user_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div>
                            <button
                              type="button"
                              onClick={() => setProfileUser(resultUser.user_id)}
                              className="font-medium text-left hover:underline hover:text-primary transition-colors"
                              data-testid={`search-name-${resultUser.user_id}`}
                            >
                              {resultUser.name}
                            </button>
                            <p className="text-sm text-muted-foreground">{resultUser.email}</p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleSendFriendRequest(resultUser.user_id)}
                          >
                            <UserPlus className="w-4 h-4 mr-1" />
                            {t('community.add')}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Friends List */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('community.myFriends')} ({friends.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {friends.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">
                      {t('community.noFriends')}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {friends.map((friend) => (
                        <div key={friend.user_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div>
                            <button
                              type="button"
                              onClick={() => setProfileUser(friend.user_id)}
                              className="font-medium text-left hover:underline hover:text-primary transition-colors"
                              data-testid={`friend-name-${friend.user_id}`}
                            >
                              {friend.name}
                            </button>
                            <p className="text-sm text-muted-foreground">{friend.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Following Tab */}
          <TabsContent value="following">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('community.following')} ({following.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {following.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">
                      {t('community.notFollowingAnyone')}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {following.map((followedUser) => (
                        <div key={followedUser.user_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div>
                            <button
                              type="button"
                              onClick={() => setProfileUser(followedUser.user_id)}
                              className="font-medium text-left hover:underline hover:text-primary transition-colors"
                              data-testid={`following-name-${followedUser.user_id}`}
                            >
                              {followedUser.name}
                            </button>
                            <p className="text-sm text-muted-foreground">{followedUser.email}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUnfollowUser(followedUser.user_id)}
                          >
                            <UserMinus className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t('community.followers')} ({followers.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {followers.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">
                      {t('community.noFollowers')}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {followers.map((follower) => (
                        <div key={follower.user_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div>
                            <button
                              type="button"
                              onClick={() => setProfileUser(follower.user_id)}
                              className="font-medium text-left hover:underline hover:text-primary transition-colors"
                              data-testid={`follower-name-${follower.user_id}`}
                            >
                              {follower.name}
                            </button>
                            <p className="text-sm text-muted-foreground">{follower.email}</p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleFollowUser(follower.user_id)}
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Privacy Tab */}
          <TabsContent value="churches">
            <ChurchesView />
          </TabsContent>

          <TabsContent value="privacy">
            <Card>
              <CardHeader>
                <CardTitle>{t('community.privacySettings')}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {t('community.privacyDesc')}
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div>
                      <p className="font-medium">{t('community.privHighlights')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('community.privHighlightsDesc')}
                      </p>
                    </div>
                    <Switch
                      checked={privacy.share_highlights}
                      onCheckedChange={(val) => handleUpdatePrivacy('share_highlights', val)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div>
                      <p className="font-medium">{t('community.privVerseImages')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('community.privVerseImagesDesc')}
                      </p>
                    </div>
                    <Switch
                      checked={privacy.share_verse_images}
                      onCheckedChange={(val) => handleUpdatePrivacy('share_verse_images', val)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div>
                      <p className="font-medium">{t('community.privStreaks')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('community.privStreaksDesc')}
                      </p>
                    </div>
                    <Switch
                      checked={privacy.share_reading_streak}
                      onCheckedChange={(val) => handleUpdatePrivacy('share_reading_streak', val)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div>
                      <p className="font-medium">{t('community.privNotes')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('community.privNotesDesc')}
                      </p>
                    </div>
                    <Switch
                      checked={privacy.share_notes}
                      onCheckedChange={(val) => handleUpdatePrivacy('share_notes', val)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div>
                      <p className="font-medium">{t('community.privPlans')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('community.privPlansDesc')}
                      </p>
                    </div>
                    <Switch
                      checked={privacy.share_reading_plans}
                      onCheckedChange={(val) => handleUpdatePrivacy('share_reading_plans', val)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div>
                      <p className="font-medium">{t('community.privPrayers')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('community.privPrayersDesc')}
                      </p>
                    </div>
                    <Switch
                      checked={privacy.share_prayers}
                      onCheckedChange={(val) => handleUpdatePrivacy('share_prayers', val)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <ProfileSheet
        userId={profileUser}
        open={!!profileUser}
        onClose={() => setProfileUser(null)}
        language={language}
      />
      {dialog}
    </div>
  );
};

export default CommunityPage;
